import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import client from "../../../api/camasirhaneApi";
import { useAuth } from "../context/CamasirhaneRoleContext";
import { usePeriodStatus } from "../hooks/usePeriodStatus";
import { canWrite, isAdmin } from "../utils/auth";
import {
  MONTHS,
  YEARS,
  WEEKDAYS_SHORT,
  daysInMonth,
  formatMaterialLabel,
  formatNum,
  formatInteger,
  getWeighingAvgCellHighlight,
} from "../utils/constants";
import { cellNumericValue, commitCellExpression, parseCellInput } from "../utils/cellExpression";
import AvgHighlightFilter from "./AvgHighlightFilter";
import "../styles/shared.css";

export default function ProductionEntryScreen() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const admin = isAdmin(user);

  const [hotels, setHotels] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hotelId, setHotelId] = useState(null);
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(7);
  const [values, setValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [avgHighlightFilters, setAvgHighlightFilters] = useState(["below"]);

  const { isClosed } = usePeriodStatus(year, month);
  const readOnly = !writable || (isClosed && !admin);
  const days = useMemo(() => Array.from({ length: daysInMonth(year, month) }, (_, i) => i + 1), [year, month]);

  const dayMeta = useMemo(() => {
    const now = new Date();
    const isCurrentMonth = now.getFullYear() === year && now.getMonth() + 1 === month;
    const todayDate = now.getDate();
    return days.map((d) => {
      const dow = new Date(year, month - 1, d).getDay();
      return {
        day: d,
        label: WEEKDAYS_SHORT[dow],
        isWeekend: dow === 0 || dow === 6,
        isToday: isCurrentMonth && d === todayDate,
      };
    });
  }, [days, year, month]);

  const bodyXRef = useRef(null);
  const headXRef = useRef(null);
  const barScrollRef = useRef(null);
  const hBarWrapRef = useRef(null);
  const gridWrapperRef = useRef(null);
  const layoutSyncFrameRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [showHBar, setShowHBar] = useState(false);

  useEffect(() => {
    Promise.all([client.get("/hotels/"), client.get("/materials/"), client.get("/material-categories/")])
      .then(([h, m, c]) => { setHotels(h.data); setMaterials(m.data); setCategories(c.data); if (h.data.length) setHotelId(h.data[0].id); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!hotelId) return;
    setValues({});
    setOriginalValues({});
    client.get("/production-records/", { params: { hotel: hotelId, year, month } })
      .then((res) => {
        const next = {};
        res.data.forEach((rec) => { next[`${rec.material_id}|${rec.day}`] = rec.quantity; });
        setValues(next);
        setOriginalValues(next);
      }).catch(() => {});
  }, [hotelId, year, month]);

  const groupedMaterials = useMemo(() => {
    const sorted = [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    return sorted.map((cat) => ({
      category: cat,
      materials: materials.filter((m) => m.category_id === cat.id && m.is_active !== false),
    })).filter((g) => g.materials.length > 0);
  }, [categories, materials]);

  const runTableLayoutSync = () => {
    const bodyX = bodyXRef.current;
    const headX = headXRef.current;
    const wrapper = gridWrapperRef.current;
    if (!bodyX) return;

    const bodyTable = bodyX.querySelector("table.data-grid--production");
    const headTable = headX?.querySelector("table.data-grid--production");
    const tables = [headTable, bodyTable].filter(Boolean);
    if (!bodyTable) return;

    const matCells = bodyTable.querySelectorAll("td.mat-cell");
    let matW = 200;
    matCells.forEach((cell) => {
      matW = Math.max(matW, cell.scrollWidth);
    });
    const gramajW = 88;

    if (wrapper) {
      wrapper.style.setProperty("--production-mat-w", `${matW}px`);
      wrapper.style.setProperty("--production-gramaj-w", `${gramajW}px`);
    }

    tables.forEach((table) => {
      const cols = table.querySelectorAll("col");
      if (cols[0]) cols[0].style.width = `${matW}px`;
      if (cols[1]) cols[1].style.width = `${gramajW}px`;
      table.querySelectorAll("th.mat-col, td.mat-cell").forEach((el) => {
        el.style.width = `${matW}px`;
        el.style.minWidth = `${matW}px`;
      });
      table.querySelectorAll("th.gramaj-col, td.gramaj-cell").forEach((el) => {
        el.style.width = `${gramajW}px`;
        el.style.minWidth = `${gramajW}px`;
      });
      const w = table.scrollWidth;
      table.style.width = `${w}px`;
      table.style.minWidth = `${w}px`;
    });

    const w = bodyTable.scrollWidth;
    setScrollWidth((prev) => (prev === w ? prev : w));
    const nextShowHBar = w > bodyX.clientWidth + 1;
    setShowHBar((prev) => (prev === nextShowHBar ? prev : nextShowHBar));
  };

  const scheduleTableLayoutSync = () => {
    if (layoutSyncFrameRef.current != null) return;
    layoutSyncFrameRef.current = requestAnimationFrame(() => {
      layoutSyncFrameRef.current = null;
      runTableLayoutSync();
    });
  };

  useEffect(() => {
    const bodyX = bodyXRef.current;
    const headX = headXRef.current;
    if (!bodyX) return;

    scheduleTableLayoutSync();
    const ro = new ResizeObserver(() => scheduleTableLayoutSync());
    ro.observe(bodyX);
    if (headX) ro.observe(headX);
    window.addEventListener("resize", scheduleTableLayoutSync);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", scheduleTableLayoutSync);
      if (layoutSyncFrameRef.current != null) {
        cancelAnimationFrame(layoutSyncFrameRef.current);
        layoutSyncFrameRef.current = null;
      }
    };
  }, [dayMeta.length, groupedMaterials.length, materials.length]);

  useEffect(() => {
    scheduleTableLayoutSync();
  }, [values]);

  useEffect(() => {
    const bodyX = bodyXRef.current;
    const headX = headXRef.current;
    const bar = barScrollRef.current;
    if (!bodyX) return;

    let syncing = false;
    const sync = (left, source) => {
      if (syncing) return;
      syncing = true;
      if (source !== "body") bodyX.scrollLeft = left;
      if (headX && source !== "head") headX.scrollLeft = left;
      if (bar && source !== "bar") bar.scrollLeft = left;
      syncing = false;
    };

    const onBody = () => sync(bodyX.scrollLeft, "body");
    const onHead = () => { if (headX) sync(headX.scrollLeft, "head"); };
    const onBar = () => { if (bar) sync(bar.scrollLeft, "bar"); };

    bodyX.addEventListener("scroll", onBody, { passive: true });
    headX?.addEventListener("scroll", onHead, { passive: true });
    bar?.addEventListener("scroll", onBar, { passive: true });
    sync(bodyX.scrollLeft, "body");
    return () => {
      bodyX.removeEventListener("scroll", onBody);
      headX?.removeEventListener("scroll", onHead);
      bar?.removeEventListener("scroll", onBar);
    };
  }, [showHBar, scrollWidth, dayMeta.length, groupedMaterials.length, materials.length]);

  useEffect(() => {
    if (!showHBar) return;
    const bar = hBarWrapRef.current;
    if (!bar) return;

    const syncBarBounds = () => {
      const main = bar.closest("main");
      if (!main) return;
      const rect = main.getBoundingClientRect();
      bar.style.left = `${rect.left}px`;
      bar.style.width = `${rect.width}px`;
    };

    syncBarBounds();
    window.addEventListener("resize", syncBarBounds);
    const ro = new ResizeObserver(syncBarBounds);
    const main = bar.closest("main");
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener("resize", syncBarBounds);
      ro.disconnect();
    };
  }, [showHBar]);

  function cellKey(m, day) { return `${m}|${day}`; }
  function handleChange(m, day, raw) {
    setValues((prev) => ({ ...prev, [cellKey(m, day)]: parseCellInput(raw, { integer: true }) }));
  }

  function handleBlur(m, day) {
    const key = cellKey(m, day);
    setValues((prev) => {
      const current = prev[key];
      if (current === "" || current == null || typeof current === "number") return prev;
      return { ...prev, [key]: commitCellExpression(current, { integer: true }) };
    });
  }

  function rowTotal(m) {
    return days.reduce((s, day) => s + cellNumericValue(values[cellKey(m, day)], { integer: true }), 0);
  }

  function rowMonthlyAverage(materialId) {
    const entries = days
      .map((day) => {
        const raw = values[cellKey(materialId, day)];
        if (raw === "" || raw == null) return null;
        return cellNumericValue(raw, { integer: true });
      })
      .filter((value) => value != null);

    if (!entries.length) return null;
    return entries.reduce((sum, value) => sum + value, 0) / entries.length;
  }

  function getAvgHighlightClass(materialId, day, average) {
    if (average == null) return "";
    const raw = values[cellKey(materialId, day)];
    if (raw === "" || raw == null) return "";
    const highlight = getWeighingAvgCellHighlight(
      cellNumericValue(raw, { integer: true }),
      average,
      avgHighlightFilters
    );
    if (highlight === "below") return " below-avg-cell";
    if (highlight === "above") return " above-avg-cell";
    return "";
  }

  const rowAverageMap = useMemo(() => {
    const map = new Map();
    materials.forEach((mat) => {
      const avg = rowMonthlyAverage(mat.id);
      if (avg != null) map.set(mat.id, avg);
    });
    return map;
  }, [materials, values, days]);

  function rowKg(mat) { return rowTotal(mat.id) * Number(mat.default_weight_kg); }

  const grandQty = materials.reduce((s, m) => s + rowTotal(m.id), 0);
  const grandKg = materials.reduce((s, m) => s + rowKg(m), 0);

  const tableCols = (
    <colgroup>
      <col className="col-mat" />
      <col className="col-gramaj" />
      {dayMeta.map((dm) => <col key={dm.day} className="col-day" />)}
      <col className="col-total-qty" />
      <col className="col-total-kg" />
    </colgroup>
  );

  const tableHeadRow = (
    <tr>
      <th className="label-col mat-col">Malzeme</th>
      <th className="gramaj-col">Gramaj (kg)</th>
      {dayMeta.map((dm) => (
        <th
          key={dm.day}
          className={`day-col${dm.isWeekend ? " weekend" : ""}${dm.isToday ? " today" : ""}`}
          title={dm.isToday ? "Bugün" : undefined}
        >
          <span className="day-weekday">{dm.label}</span>
          <span className="day-number">{dm.day}</span>
        </th>
      ))}
      <th className="total-col">Adet</th>
      <th className="total-col">kg</th>
    </tr>
  );

  async function handleSave() {
    setSaving(true);
    setSaveMessage("");
    const records = [];
    const seenKeys = new Set();

    Object.entries(values).forEach(([key, quantity]) => {
      seenKeys.add(key);
      const [material_id, day] = key.split("|").map(Number);
      const base = { hotel_id: hotelId, year, month, day, material_id };
      const resolved = typeof quantity === "string" ? commitCellExpression(quantity, { integer: true }) : quantity;
      if (resolved === "" || resolved === undefined) {
        if (originalValues[key] !== undefined && originalValues[key] !== "") {
          records.push({ ...base, delete: true });
        }
        return;
      }
      const material = materials.find((m) => m.id === material_id);
      records.push({ ...base, quantity: resolved, unit_weight_kg: material?.default_weight_kg });
    });

    Object.keys(originalValues).forEach((key) => {
      if (seenKeys.has(key)) return;
      const [material_id, day] = key.split("|").map(Number);
      records.push({ hotel_id: hotelId, year, month, day, material_id, delete: true });
    });

    if (!records.length) {
      setSaveMessage("Kaydedilecek değişiklik yok.");
      setSaving(false);
      return;
    }

    try {
      const res = await client.post("/production-records/bulk/", { records });
      const refreshed = await client.get("/production-records/", { params: { hotel: hotelId, year, month } });
      const next = {};
      refreshed.data.forEach((rec) => { next[`${rec.material_id}|${rec.day}`] = rec.quantity; });
      setValues(next);
      setOriginalValues(next);
      setSaveMessage(res.data.errors?.length ? `Kısmen kaydedildi (${res.data.saved}).` : "Kaydedildi.");
    } catch (err) {
      setSaveMessage(err.response?.data?.detail || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={`screen production-screen${showHBar ? " production-screen--hbar" : ""}`}>
      <div className="production-scroll">
        <div className="screen-header">
          <h2>Üretim Adet Girişi</h2>
          <p>Malzeme bazında günlük üretim adetleri — otomatik kg hesabı</p>
        </div>

        {isClosed && <div className="locked-banner">🔒 Bu dönem kapalı.</div>}

        <div className="toolbar">
          <select className="ui-select" value={hotelId ?? ""} onChange={(e) => setHotelId(Number(e.target.value))}>
            {hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
          <select className="ui-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
          </select>
          <select className="ui-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <AvgHighlightFilter value={avgHighlightFilters} onChange={setAvgHighlightFilters} />
          {!readOnly && (
            <button className="ui-btn" onClick={handleSave} disabled={saving || !hotelId}>
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          )}
          {saveMessage && <span className="status-msg success">{saveMessage}</span>}
        </div>

        <div className="summary-cards">
          <div className="summary-card"><h3>Toplam Adet</h3><p>{formatInteger(grandQty)}</p></div>
          <div className="summary-card"><h3>Toplam kg</h3><p>{formatNum(grandKg, 2)}</p></div>
        </div>

        <div className="grid-wrapper grid-wrapper--production" ref={gridWrapperRef}>
          <div className="weighing-thead-bar">
            <div className="weighing-grid-x weighing-grid-x--head" ref={headXRef}>
              <table className="data-grid data-grid--production">
                {tableCols}
                <thead>{tableHeadRow}</thead>
              </table>
            </div>
          </div>
          <div className="weighing-grid-x weighing-grid-x--body" ref={bodyXRef}>
            <table className="data-grid data-grid--production">
              {tableCols}
              <tbody>
                {groupedMaterials.map(({ category, materials: catMats }) => (
                  <Fragment key={category.id}>
                    <tr className="category-header">
                      <td colSpan={dayMeta.length + 4}>{category.name}</td>
                    </tr>
                    {catMats.map((mat) => {
                      const rowAvg = rowAverageMap.get(mat.id);
                      return (
                      <tr key={mat.id}>
                        <td className="sticky-col label-col mat-cell">{formatMaterialLabel(mat.name)}</td>
                        <td className="gramaj-cell">{formatNum(mat.default_weight_kg, 3)}</td>
                        {dayMeta.map((dm) => {
                          const avgHighlightClass = getAvgHighlightClass(mat.id, dm.day, rowAvg);
                          return (
                          <td
                            key={dm.day}
                            className={`day-cell${dm.isWeekend ? " weekend-cell" : ""}${dm.isToday ? " today-cell" : ""}${avgHighlightClass}`}
                          >
                            <input type="text" inputMode="numeric" disabled={readOnly}
                              value={values[cellKey(mat.id, dm.day)] ?? ""}
                              onChange={(e) => handleChange(mat.id, dm.day, e.target.value)}
                              onBlur={() => handleBlur(mat.id, dm.day)} />
                          </td>
                          );
                        })}
                        <td className="total-col">{formatInteger(rowTotal(mat.id))}</td>
                        <td className="total-col">{formatNum(rowKg(mat), 2)}</td>
                      </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="grand-row">
                  <td className="sticky-col label-col grand-label" colSpan={2}>Genel toplam</td>
                  {dayMeta.map((dm) => {
                    let s = 0;
                    materials.forEach((m) => { s += cellNumericValue(values[cellKey(m.id, dm.day)], { integer: true }); });
                    return (
                      <td key={dm.day} className={`day-cell${dm.isWeekend ? " weekend-cell" : ""}${dm.isToday ? " today-cell" : ""}`}>
                        {s ? formatInteger(s) : ""}
                      </td>
                    );
                  })}
                  <td className="total-col grand-total">{formatInteger(grandQty)}</td>
                  <td className="total-col grand-total">{formatNum(grandKg, 2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {showHBar && (
        <div className="weighing-hscroll-bar" ref={hBarWrapRef} aria-hidden="true">
          <div className="weighing-hscroll-track" ref={barScrollRef}>
            <div className="weighing-hscroll-inner" style={{ width: scrollWidth }} />
          </div>
        </div>
      )}
    </div>
  );
}
